/* tslint:disable:no-identical-functions */
import { Injectable, OnDestroy } from '@angular/core';
import { CBASKET_NAMESPACE, SortParams } from '@epicuro-next/data/baskets';
import { ExtendedDtoBasket } from '@epicuro-next/data/baskets-dto';
import { BasketMappingSettings } from '@epicuro-next/data/baskets-grouping';
import { getGroupedBasketWorker } from '@epicuro-next/data/baskets-grouping-worker';
import { initConfigSuccess } from '@epicuro-next/platform/configuration';
import { EnvironmentService } from '@epicuro-next/platform/environment';
import { isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import {
  Basket,
  BasketRowsGroupType,
} from 'graphql-api/baskets-api-interfaces';
import {
  combineLatest,
  forkJoin,
  Observable,
  of,
  ReplaySubject,
  Subject,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';

import { CBasketConfigurations } from '../../configurations';
import {
  getNextSortingState,
  getSortParams,
  mapSortingByValueColumn,
} from '../../shared';
import * as basketActions from '../actions/';
import { groupDtoBasketFailure, groupDtoBasketSuccess } from '../actions/';
import {
  selectAllBasketManualDiscounts,
  selectAllDtoBasketsFromEntitiesByBasketId,
  selectBasketMappingSettings,
  selectDefaultSortConfiguration,
  selectGroupByOptionsRule,
  selectHasSortingThirdState,
  selectIsBasketStatusNotNew,
  selectSortParameters,
  selectSortPerGroupConfiguration,
} from '../selectors';

type CalculationResultType =
  | ReturnType<typeof groupDtoBasketSuccess>
  | ReturnType<typeof groupDtoBasketFailure>;

type CalculationTriggerType = {
  basket: ExtendedDtoBasket;
  basketMappingSettings: Partial<BasketMappingSettings>;
  groupTypes: BasketRowsGroupType[];
};

type CalculationResultObjType = {
  basketId: string;
  result: CalculationResultType;
};

@Injectable()
export class BasketGroupingEffects implements OnDestroy {
  constructor(
    private actions$: Actions,
    private store: Store,
    private environmentService: EnvironmentService,
  ) {}
  private readonly destroy$ = new Subject<void>();

  private basketItemDivisionAttributeKey$ =
    this.environmentService.environmentSettings.pipe(
      map((value) => value?.basketItemDivisionAttributeKey ?? 'divisionb2b'),
    );

  private basketMappingSettings$: Observable<Partial<BasketMappingSettings>> =
    combineLatest([
      this.store.pipe(select(selectBasketMappingSettings)),
      this.basketItemDivisionAttributeKey$,
      this.store.pipe(
        select(selectAllBasketManualDiscounts),
        map((mds) => mds.filter((md) => md.currentValue !== null)),
      ),
    ]).pipe(
      map(([settings, basketItemDivisionAttributeKey, manualDiscounts]) => {
        const manualDiscountsMap: {
          [basketItemId: string]: { currentValue: number };
        } = {};
        manualDiscounts.forEach((entity) => {
          const discount = entity.currentValue;
          if (discount !== null) {
            manualDiscountsMap[entity.basketItemId] = {
              currentValue: discount,
            };
          }
        });
        return {
          ...settings,
          additionalPrefix: settings.additionalPrefix,
          basketItemDivisionAttributeKey,
          manualDiscounts: manualDiscountsMap,
        };
      }),
    );

  public setDefaultSorting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initConfigSuccess),
      filter(({ configurations }) =>
        Boolean(configurations[CBASKET_NAMESPACE] as CBasketConfigurations),
      ),
      switchMap(() =>
        this.store.pipe(
          select(selectDefaultSortConfiguration),
          take(1),
          map((sorting) =>
            basketActions.initSortingBasketItems({
              sorting,
            }),
          ),
        ),
      ),
    ),
  );

  private readonly calculationStreamsByBasketId: {
    [basketId: string]: {
      trigger: Subject<CalculationTriggerType>;
    };
  } = {};

  private readonly calculationResults$ =
    new Subject<CalculationResultObjType>();

  public groupBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        basketActions.applyBasketManualDiscountsValidated,
        basketActions.loadBasketManualDiscounts,
        basketActions.loadDtoBasketSuccess,
        basketActions.manualDiscountsUpdatedFailed,
        basketActions.clearBasketManualDiscounts,
        basketActions.resetBasketManualDiscountsInputs,
        basketActions.removeBasketManualDiscounts,
      ),
      concatLatestFrom(() => [
        this.store.pipe(
          select(selectAllDtoBasketsFromEntitiesByBasketId),
          filter(isNonNullish),
        ),
        this.store.pipe(select(selectGroupByOptionsRule), filter(isNonNullish)),
        this.basketMappingSettings$,
      ]),
      mergeMap(
        ([
          action,
          allDtoBasketsByBasketId,
          groupTypes,
          basketMappingSettings,
        ]) => {
          const basketId = this.getBasketId(action);
          const basket = allDtoBasketsByBasketId[basketId] as ExtendedDtoBasket;
          this.filterManualDiscountsByApplicableBasketItems(
            basket,
            basketMappingSettings,
          );

          const calculationStream = this.getCalculationStream(basketId);

          calculationStream.trigger.next({
            basket,
            basketMappingSettings,
            groupTypes,
          });

          return this.calculationResults$.pipe(
            filter((calculationResult) => {
              return calculationResult.basketId === basketId;
            }),
            take(1),
            map(({ result }) => result),
          );
        },
      ),
    ),
  );

  public mapGroupingChangeToSorting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.basketGroupingChanged),
      concatLatestFrom(() => [
        this.store.pipe(select(selectSortPerGroupConfiguration)),
        this.store.pipe(select(selectSortParameters)),
      ]),
      map(([{ groupingType }, sortPerGroupingMap, currentSortParams]) => {
        const [type, direction] = sortPerGroupingMap[groupingType] || [];

        return getSortParams(currentSortParams, { type, direction });
      }),
      map((sorting) => basketActions.applySortingBasketItems({ sorting })),
    ),
  );

  public changeBasketSorting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(basketActions.triggerSortingBasketItems),
      concatLatestFrom(() => [
        this.store.pipe(select(selectSortParameters)),
        this.store.pipe(select(selectHasSortingThirdState)),
        this.store.pipe(select(selectDefaultSortConfiguration)),
        this.store.pipe(select(selectIsBasketStatusNotNew)),
      ]),
      map(
        ([
          { sorting: initialSorting },
          currentState,
          hasThirdState,
          defaultParams,
          isPlacedBasket,
        ]): SortParams => {
          const sorting = mapSortingByValueColumn(
            initialSorting,
            isPlacedBasket,
          );

          return getNextSortingState(
            currentState,
            sorting,
            hasThirdState,
            defaultParams,
          );
        },
      ),
      map(({ type, direction }) =>
        basketActions.applySortingBasketItems({
          sorting: {
            type,
            direction,
          },
        }),
      ),
    ),
  );

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getCalculationStream(basketId: string) {
    if (this.calculationStreamsByBasketId[basketId]) {
      // This means a calculation is running, by reusing this trigger
      // the switchMap comes into effect to cancel the previous calculation.
      return this.calculationStreamsByBasketId[basketId];
    }

    const trigger$ = new ReplaySubject<CalculationTriggerType>(1);
    const result$ = trigger$.pipe(
      // This switchMap takes care that ongoing basket calculations are cancelled.
      // And only the last calculationArg (and thus results) are continued with.
      switchMap((calculationArgs) => {
        return this.handleGroupedBasketWorker(
          calculationArgs.basket,
          calculationArgs.groupTypes,
          calculationArgs.basketMappingSettings,
        ).pipe(
          map((result) => {
            const results: CalculationResultObjType = {
              basketId: calculationArgs.basket.basketId,
              result,
            };

            return results;
          }),
          map((calculationResultObj) => {
            const worker =
              this.calculationStreamsByBasketId[calculationResultObj.basketId];
            worker.trigger.complete();

            // Makes sure the dictionary is cleaned up and no streams linger around
            delete this.calculationStreamsByBasketId[
              calculationResultObj.basketId
            ];
            return calculationResultObj;
          }),
        );
      }),
    );

    result$.pipe(take(1), takeUntil(this.destroy$)).subscribe((result) => {
      // When the results of the last requested calculations come in (earlier requests are cancelled)
      // it is put on the stream to which all calculations results of any basket are emitted.
      this.calculationResults$.next(result);
    });

    this.calculationStreamsByBasketId[basketId] = {
      trigger: trigger$,
    };

    return this.calculationStreamsByBasketId[basketId];
  }

  private handleGroupedBasketWorker(
    basket: ExtendedDtoBasket,
    groupTypes: BasketRowsGroupType[],
    basketMappingSettings: Partial<BasketMappingSettings>,
  ): Observable<CalculationResultType> {
    const initialValue: { [key: string]: Observable<Basket | null> } = {};
    const groupedBasketsCollection = groupTypes.reduce(
      (acc, groupType) => ({
        ...acc,
        [groupType]: getGroupedBasketWorker(
          of({
            basket,
            groupType,
            settings: basketMappingSettings,
          }),
        ),
      }),
      initialValue,
    );

    return forkJoin(groupedBasketsCollection).pipe(
      map((groupedBaskets) => {
        if (Object.values(groupedBaskets).some((grouped) => !grouped)) {
          throw new Error('Failed to group basket');
        }
        return basketActions.groupDtoBasketSuccess({ groupedBaskets });
      }),
      catchError((err) =>
        of(
          basketActions.groupDtoBasketFailure({
            error: err.message,
            basketId: basket.basketId,
          }),
        ),
      ),
    );
  }

  private getBasketId(
    action: { basketId: string } | { basket: { basketId: string } },
  ): string {
    if ('basketId' in action) {
      return action.basketId;
    } else {
      return action.basket.basketId;
    }
  }

  private filterManualDiscountsByApplicableBasketItems(
    basket: ExtendedDtoBasket,
    basketMappingSettings: Partial<BasketMappingSettings>,
  ): void {
    if (basketMappingSettings.manualDiscounts) {
      const applicableBasketItemIds = basket.items.map((item) => item.itemId);
      const manualDiscounts: {
        [basketItemId: string]: { currentValue: number };
      } = {};
      Object.entries(basketMappingSettings.manualDiscounts).forEach(
        ([basketItemId, manualDiscountChange]) => {
          if (applicableBasketItemIds.includes(basketItemId)) {
            manualDiscounts[basketItemId] = manualDiscountChange;
          }
        },
      );

      basketMappingSettings.manualDiscounts = manualDiscounts;
    }
  }
}
